import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import BalbisLogo from "@/components/BalbisLogo";

export default function PortalLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role?.startsWith("client_")) navigate("/portal");
    else if (user?.role?.startsWith("agency_")) navigate("/dashboard");
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      if (!u.role?.startsWith("client_")) {
        toast.error("This portal is for clients only. Balbis staff should use the main login.");
        return;
      }
      navigate("/portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#FBF9F6" }}>
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-12" style={{ background: "#F5F3EF" }}>
        <BalbisLogo className="h-8 w-auto" style={{ color: "#292524" }} />
        <div>
          <h1 className="font-serif text-4xl font-bold leading-tight mb-4" style={{ color: "#292524" }}>
            Client Portal
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "#57534E" }}>
            Submit tasks, track progress, and stay in touch with the Balbis team.
          </p>
        </div>
        <p className="text-sm" style={{ color: "#C7C3BF" }}>Balbis Studio</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="font-serif text-3xl font-bold mb-2" style={{ color: "#292524" }}>Welcome back</h2>
            <p className="text-sm" style={{ color: "#57534E" }}>Sign in to your client portal</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#57534E" }}>Email</label>
              <input
                data-testid="portal-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-amber-200"
                style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#57534E" }}>Password</label>
              <div className="relative">
                <input
                  data-testid="portal-password-input"
                  type={showPass ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-amber-200"
                  style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#A8A29E" }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="portal-login-submit"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-60"
              style={{ background: "#D97706", color: "#fff" }}
            >
              {loading ? "Signing in..." : "Sign In to Portal"}
            </button>
          </form>
          <p className="text-xs text-center mt-6" style={{ color: "#A8A29E" }}>
            Balbis staff?{" "}
            <a href="/admin" className="underline" style={{ color: "#292524" }}>Sign in here</a>
          </p>
        </div>
      </div>
    </div>
  );
}
